import { useState } from "react";
import { Sparkles, ChevronDown, ChevronUp, X, Quote, RotateCcw, Wand2, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Amber info-panel shown at the top of the ResumeEditor when the variant
 * was created by the "generate AI-CV from a vacancy" flow. Explains WHICH
 * sections the AI rewrote and — thanks to `grounded_in` snippets from
 * `letter_ai.ai_rewrite_resume_for_match` — WHY each rewrite is honest
 * (verbatim source-CV evidence per change).
 *
 * Dismissal is per-resume-id via localStorage so the panel becomes a
 * one-time onboarding for that specific AI-generated variant.
 */
export default function AiGeneratedInfoPanel({ resumeId, generatedFromJob, meta, onRevertProfile, onRevertExperience, onRegenerated }) {
  const { t } = useT();
  const dismissKey = `jobgrid_ai_variant_dismissed_${resumeId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);
  const [focusBusy, setFocusBusy] = useState(null);

  if (!generatedFromJob || dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const regenerate = async (focus) => {
    if (focusBusy) return;
    setFocusBusy(focus);
    try {
      const { data } = await api.post(`/resumes/${resumeId}/regenerate-with-focus`, { focus });
      toast.success(t("aiInfoRegenerated"));
      onRegenerated?.(data);
      setExpanded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("aiInfoRegenFailed"));
    } finally {
      setFocusBusy(null);
    }
  };

  const profChanged = !!meta?.profile?.new;
  const expChanged = (meta?.experiences || []).length;
  const totalChanges = (profChanged ? 1 : 0) + expChanged;

  return (
    <div
      data-testid="ai-variant-info-panel"
      className="border border-amber-600 bg-amber-50 mb-6"
    >
      <div className="p-4 flex items-start gap-3">
        <Sparkles size={16} strokeWidth={1.5} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base leading-tight mb-1 text-amber-900">
            {t("aiInfoTitle", { job: generatedFromJob.job_title })}
          </p>
          <p className="text-sm text-amber-900/80 leading-relaxed">
            {t("aiInfoSubtitle", { n: totalChanges })}
          </p>
          {totalChanges > 0 && (
            <button
              data-testid="ai-variant-info-toggle"
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-900 hover:text-amber-700 inline-flex items-center gap-1"
            >
              {expanded ? <><ChevronUp size={11} /> {t("aiInfoHide")}</> : <><ChevronDown size={11} /> {t("aiInfoShow")}</>}
            </button>
          )}
        </div>
        <button
          data-testid="ai-variant-info-dismiss"
          type="button"
          onClick={dismiss}
          title={t("aiInfoDismiss")}
          className="p-1 text-amber-700 hover:text-amber-900 shrink-0"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>

      {expanded && totalChanges > 0 && (
        <div className="border-t border-amber-300 bg-white/50 p-4 space-y-4">
          {profChanged && (
            <ChangeCard
              label={t("aiInfoProfileLabel")}
              oldText={meta.profile.old}
              newText={meta.profile.new}
              grounded={meta.profile.grounded_in || []}
              onRevert={onRevertProfile}
              testId="ai-info-profile"
              t={t}
            />
          )}
          {(meta.experiences || []).map((e) => (
            <ChangeCard
              key={e.id}
              label={`${e.role || t("aiInfoExperience")} · ${e.company || ""}`.trim()}
              oldText={e.old}
              newText={e.new}
              grounded={e.grounded_in || []}
              onRevert={onRevertExperience ? () => onRevertExperience(e.id, e.old) : null}
              testId={`ai-info-exp-${e.id}`}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Focus regenerate chips */}
      <div className="border-t border-amber-300 bg-amber-50/60 p-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-900 mr-1">
          {t("aiInfoFocusLabel")}
        </span>
        {[
          ["leadership", "aiInfoFocusLeadership"],
          ["concise", "aiInfoFocusConcise"],
          ["metrics", "aiInfoFocusMetrics"],
        ].map(([key, labelKey]) => (
          <button
            key={key}
            data-testid={`ai-info-focus-${key}`}
            type="button"
            onClick={() => regenerate(key)}
            disabled={!!focusBusy}
            className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-amber-800 bg-white text-amber-900 hover:bg-amber-800 hover:text-white transition-colors px-2.5 py-1 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {focusBusy === key
              ? <Loader2 size={11} strokeWidth={1.75} className="animate-spin" />
              : <Wand2 size={11} strokeWidth={1.75} />}
            {t(labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChangeCard({ label, oldText, newText, grounded, onRevert, testId, t }) {
  return (
    <div data-testid={testId} className="border border-amber-300 bg-white p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="ed-eyebrow text-amber-900">{label}</p>
        {onRevert && (
          <button
            data-testid={`${testId}-revert`}
            type="button"
            onClick={onRevert}
            className="font-mono text-[0.55rem] uppercase tracking-[0.15em] border border-neutral-300 text-neutral-600 hover:border-neutral-900 hover:text-neutral-900 px-1.5 py-0.5 inline-flex items-center gap-1"
            title={t("aiInfoRevertHint")}
          >
            <RotateCcw size={10} strokeWidth={1.75} /> {t("aiInfoRevert")}
          </button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 mb-1">{t("aiInfoBefore")}</p>
          <p className="text-neutral-600 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{oldText || "—"}</p>
        </div>
        <div>
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-emerald-700 mb-1">{t("aiInfoAfter")}</p>
          <p className="text-neutral-900 whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{newText || "—"}</p>
        </div>
      </div>
      {grounded.length > 0 && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 mb-1.5 flex items-center gap-1">
            <Quote size={9} strokeWidth={1.75} /> {t("aiInfoGrounded")}
          </p>
          <ul className="space-y-1">
            {grounded.map((snip, i) => (
              <li key={i} className="text-xs italic text-neutral-700 leading-snug">“{snip}”</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
